/**
 * Validation utilities for form inputs
 * Provides regex patterns and validation functions for common input types
 */

// Email validation regex - RFC 5322 simplified
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phone validation - supports international formats
export const PHONE_REGEX =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

// Name validation - allows letters, spaces, hyphens, and apostrophes
export const NAME_REGEX = /^[a-zA-Z0-9\s'-]{2,}$/;

// Title validation - allows letters, numbers, spaces, and common punctuation
export const TITLE_REGEX = /^[a-zA-Z0-9\s'"\-.,!?()&:/$%]{1,200}$/;

/**
 * Validation error messages
 */
export const VALIDATION_MESSAGES = {
  REQUIRED: "This field is required",
  EMAIL_INVALID: "Please enter a valid email address",
  EMAIL_REQUIRED: "Enter an email address",
  PHONE_INVALID: "Please enter a valid phone number",
  NAME_TOO_SHORT: "Name must be at least 2 characters",
  NAME_INVALID: "Please enter a valid name",
  MIN_LENGTH: (min: number) => `Must be at least ${min} characters`,
  MAX_LENGTH: (max: number) => `Must not exceed ${max} characters`,
} as const;

/**
 * Generates validation messages with dynamic content
 * @param fieldName - The field name to include in the messages (e.g., "First name", "Last name", "Full name")
 * @returns An object containing validation messages
 */
export function validateMessages(fieldName: string) {
  return {
    REQUIRED: `${fieldName} is required`,
    EMAIL_INVALID: "Please enter a valid email address",
    EMAIL_REQUIRED: "Enter an email address",
    PHONE_INVALID: "Please enter a valid phone number",
    NAME_TOO_SHORT: `${fieldName} must be at least 2 characters`,
    NAME_INVALID: `Please enter a valid ${fieldName.toLowerCase()}`,
    MIN_LENGTH: (min: number) =>
      `${fieldName} must be at least ${min} characters`,
    MAX_LENGTH: (max: number) =>
      `${fieldName} must not exceed ${max} characters`,
  };
}

/**
 * Validation function type
 */
export type ValidationResult = {
  isValid: boolean;
  error?: string;
};

/**
 * Validates an email address
 * @param params - Validation parameters
 * @param params.email - The email to validate
 * @param params.customError - Optional custom error message
 * @returns ValidationResult
 */
export const validateEmail = ({
  email,
  customError,
}: {
  email: string;
  customError?: string;
}): ValidationResult => {
  if (!email || email.trim() === "") {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.EMAIL_REQUIRED,
    };
  }

  if (!EMAIL_REGEX.test(email.trim())) {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.EMAIL_INVALID,
    };
  }

  return { isValid: true };
};

/**
 * Validates a phone number
 * @param params - Validation parameters
 * @param params.phone - The phone number to validate
 * @param params.customError - Optional custom error message
 * @returns ValidationResult
 */
export const validatePhone = ({
  phone,
  customError,
}: {
  phone: string;
  customError?: string;
}): ValidationResult => {
  if (!phone || phone.trim() === "") {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.REQUIRED,
    };
  }

  if (!PHONE_REGEX.test(phone.trim())) {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.PHONE_INVALID,
    };
  }

  return { isValid: true };
};

/**
 * Validates a name field
 * @param params - Validation parameters
 * @param params.name - The name to validate
 * @param params.minLength - Minimum length (default: 2)
 * @param params.customError - Optional custom error message
 * @param params.fieldLabel - Optional field label for dynamic error messages (e.g., "First name")
 * @returns ValidationResult
 */
export const validateName = ({
  name,
  minLength = 2,
  customError,
  fieldLabel,
}: {
  name: string;
  minLength?: number;
  customError?: string;
  fieldLabel?: string;
}): ValidationResult => {
  const messages = fieldLabel
    ? validateMessages(fieldLabel)
    : VALIDATION_MESSAGES;

  if (!name || name.trim() === "") {
    return {
      isValid: false,
      error: customError || messages.REQUIRED,
    };
  }

  if (name.trim().length < minLength) {
    return {
      isValid: false,
      error: customError || messages.MIN_LENGTH(minLength),
    };
  }

  if (!NAME_REGEX.test(name.trim())) {
    return {
      isValid: false,
      error: customError || messages.NAME_INVALID,
    };
  }

  return { isValid: true };
};

/**
 * Validates a title field (allows alphanumeric and common punctuation)
 * @param params - Validation parameters
 * @param params.title - The title to validate
 * @param params.minLength - Minimum length (default: 2)
 * @param params.customError - Optional custom error message
 * @param params.fieldLabel - Optional field label for dynamic error messages (e.g., "Playlist title")
 * @returns ValidationResult
 */
export const validateTitle = ({
  title,
  minLength = 2,
  customError,
  fieldLabel,
}: {
  title: string;
  minLength?: number;
  customError?: string;
  fieldLabel?: string;
}): ValidationResult => {
  const messages = fieldLabel
    ? validateMessages(fieldLabel)
    : VALIDATION_MESSAGES;

  if (!title || title.trim() === "") {
    return {
      isValid: false,
      error: customError || messages.REQUIRED,
    };
  }

  if (title.trim().length < minLength) {
    return {
      isValid: false,
      error: customError || messages.MIN_LENGTH(minLength),
    };
  }

  if (!TITLE_REGEX.test(title.trim())) {
    return {
      isValid: false,
      error:
        customError ||
        `Please enter a valid ${
          fieldLabel ? fieldLabel.toLowerCase() : "title"
        }`,
    };
  }

  return { isValid: true };
};

/**
 * Validates required field
 * @param params - Validation parameters
 * @param params.value - The value to validate
 * @param params.customError - Optional custom error message
 * @returns ValidationResult
 */
export const validateRequired = ({
  value,
  customError,
}: {
  value: string;
  customError?: string;
}): ValidationResult => {
  if (!value || value.trim() === "") {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.REQUIRED,
    };
  }

  return { isValid: true };
};

/**
 * Validates minimum length
 * @param params - Validation parameters
 * @param params.value - The value to validate
 * @param params.minLength - Minimum length required
 * @param params.customError - Optional custom error message
 * @returns ValidationResult
 */
export const validateMinLength = ({
  value,
  minLength,
  customError,
}: {
  value: string;
  minLength: number;
  customError?: string;
}): ValidationResult => {
  if (value.trim().length < minLength) {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.MIN_LENGTH(minLength),
    };
  }

  return { isValid: true };
};

/**
 * Validates maximum length
 * @param params - Validation parameters
 * @param params.value - The value to validate
 * @param params.maxLength - Maximum length allowed
 * @param params.customError - Optional custom error message
 * @returns ValidationResult
 */
export const validateMaxLength = ({
  value,
  maxLength,
  customError,
}: {
  value: string;
  maxLength: number;
  customError?: string;
}): ValidationResult => {
  if (value.trim().length > maxLength) {
    return {
      isValid: false,
      error: customError || VALIDATION_MESSAGES.MAX_LENGTH(maxLength),
    };
  }

  return { isValid: true };
};

/**
 * Validates using a custom regex pattern
 * @param params - Validation parameters
 * @param params.value - The value to validate
 * @param params.pattern - Regex pattern to test against
 * @param params.customError - Optional custom error message
 * @returns ValidationResult
 */
export const validatePattern = ({
  value,
  pattern,
  customError,
}: {
  value: string;
  pattern: RegExp;
  customError?: string;
}): ValidationResult => {
  if (!pattern.test(value)) {
    return {
      isValid: false,
      error: customError || "Invalid format",
    };
  }

  return { isValid: true };
};
